import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  CompraAnimalesItem,
  CompraAnimalesService,
} from "../../../../generated/grpc/pesaje_ganaderia_pb";
import { grpcCompraAnimalesDatabase } from "../database/GrpcCompraAnimalesDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import {
  fx_nombreLote,
  fx_nombresLotesAsync,
  toLoteResumen,
} from "../../../terceros/services/LoteResumenUtils";
import { CompraAnimalesResponse } from "../response/CompraAnimalesResponse";

/** Variante gRPC-Web (con delta real, por finca) de `CompraAnimalesGetListByFincaIdService`
 *  (ver ese archivo). Devuelve `CompraAnimalesResponse[]` -mismo shape que ya
 *  usa el resto de la app-. Rellena con valores inertes los campos sin uso
 *  real (`clienteId`, `tipoAnimalId`, `razaAnimalId`,
 *  `cantidadAnimalesVendidos`) y arma `loteEntrada`/`loteActual` como
 *  `ClienteFincaLoteResponse` de relleno (`toLoteResumen`) — el proto ya no
 *  manda el lote completo, solo id+nombre (ver el comentario de
 *  `CompraAnimalesItem` en el proto). */
class CompraAnimalesGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<CompraAnimalesResponse[]> {
    await this._syncAsync();
    const items = await grpcCompraAnimalesDatabase.getListByFincaIdAsync<CompraAnimalesItem>(
      this._fincaId,
    );
    // Nombre actual de cada potrero: la compra guarda el que tenía al sincronizarse.
    const nombresLotes = await fx_nombresLotesAsync(this._fincaId);
    return items.map((item) => toCompraAnimalesResponse(item, nombresLotes));
  }

  clearCache(): void {
    serviceSync.reset(`grpc:compras:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:compras:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcCompraAnimalesDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(CompraAnimalesService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcCompraAnimalesDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createCompraAnimalesGetListByFincaIdService`). */
export const fx_createCompraAnimalesGrpcService = createCachedFactory(
  (fincaId: string) => new CompraAnimalesGrpcService(fincaId),
);

/** Adapta un `CompraAnimalesItem` (gRPC) al shape `CompraAnimalesResponse`
 *  (REST). Exportada porque los servicios de guardado/borrado también la
 *  necesitan.
 *  @param nombresLotes Nombres actuales de los lotes (`fx_nombresLotesAsync`);
 *  sin él se usan los que trae la compra. */
export function toCompraAnimalesResponse(
  compra: CompraAnimalesItem,
  nombresLotes?: ReadonlyMap<string, string>,
): CompraAnimalesResponse {
  return {
    id: compra.id,
    isEliminado: compra.eliminado,
    clienteId: "",
    fincaId: compra.fincaId,
    loteEntrada: toLoteResumen(
      compra.loteEntradaId,
      fx_nombreLote(nombresLotes, compra.loteEntradaId, compra.loteEntradaNombre),
    ),
    loteActual: toLoteResumen(
      compra.loteActualId,
      fx_nombreLote(nombresLotes, compra.loteActualId, compra.loteActualNombre),
    ),
    tipoAnimalId: "",
    tipoAnimal: compra.tipoAnimal,
    razaAnimalId: "",
    razaAnimal: compra.razaAnimal,
    codigo: compra.codigo,
    fechaCompra: compra.fechaCompra,
    cantidadAnimalesCompra: compra.cantidadAnimalesCompra,
    cantidadAnimalesVendidos: compra.cantidadAnimalesCompra - compra.animalesDisponibles,
    animalesDisponibles: compra.animalesDisponibles,
    pesoPromedioEntrada: compra.pesoPromedioEntrada,
    pesoTotal: compra.pesoTotal,
    precioKg: compra.precioKg,
    precioTotal: compra.precioTotal,
    gdpEsperadoGr: compra.gdpEsperadoGr,
    pesoTargetSalida: compra.pesoTargetSalida,
    fechaEsperadaSalida: compra.fechaEsperadaSalida,
  };
}
